import React from "react";
import { Helmet } from "react-helmet";
import HeaderThree from "../component/header/HeaderThree";

import { Card, CardBody } from "reactstrap";
import { useTranslation } from "react-i18next";

const LANGUAGES = [
  { code: "en", labelKey: "privacy.langEnglish" },
  { code: "fr", labelKey: "privacy.langFrench" },
  { code: "de", labelKey: "privacy.langGerman" },
  { code: "nl", labelKey: "privacy.langDutch" },
];

const Html = ({ tag: Tag = "p", className, i18nKey, t }) => (
  <Tag className={className} dangerouslySetInnerHTML={{ __html: t(i18nKey) }} />
);

const DataTable = ({ headersKey, rowsKey, t }) => {
  const headers = t(headersKey, { returnObjects: true });
  const rows = t(rowsKey, { returnObjects: true });
  return (
    <div className="table-responsive">
      <table className="table privacy-policy-table">
        <thead>
          <tr>
            {headers.map((h, i) => (
              <th key={i}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i}>
              {row.map((cell, j) => (
                <td key={j}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const RightsList = ({ i18nKey, t }) => {
  const items = t(i18nKey, { returnObjects: true });
  return (
    <ul className="privacy-policy-rights">
      {items.map((item, i) => (
        <li key={i}>
          <strong className="privacy-policy-h3">{item.title}</strong>
          <p dangerouslySetInnerHTML={{ __html: item.body }} />
        </li>
      ))}
    </ul>
  );
};

const BulletList = ({ i18nKey, t }) => {
  const items = t(i18nKey, { returnObjects: true });
  return (
    <ul className="privacy-policy-list">
      {items.map((item, i) => (
        <li key={i} dangerouslySetInnerHTML={{ __html: item }} />
      ))}
    </ul>
  );
};

const LanguageSwitcher = ({ i18n }) => (
  <span className="privacy-policy-lang-switch">
    {LANGUAGES.map((lang, i) => (
      <React.Fragment key={lang.code}>
        {i > 0 && <span> | </span>}
        <button
          type="button"
          className={`privacy-policy-lang-link${i18n.language === lang.code ? " active" : ""}`}
          onClick={() => {
            i18n.changeLanguage(lang.code);
            localStorage.setItem("lang", lang.code);
          }}
        >
          {i18n.t(lang.labelKey)}
        </button>
      </React.Fragment>
    ))}
  </span>
);

const PrivacyPage = () => {
  const { t, i18n } = useTranslation();
  const icon = `${process.env.PUBLIC_URL}/assets/images/gp_images/ecoshift-logo-brand-eco-movement-ecoresponsible-ecopositive.webp`;

  window.onload = function () {
    document.querySelector(".header--fixed").classList.add("sticky");
  };

  return (
    <div className="active-dark bg_color--ugs">
      <Helmet>
        <title>Privacy policy</title>
        <meta name="description" content="EcoShift ⁕ the digital platform for your eco-positive consumption!" />
        <meta name="keywords" content="ecoshift,shift,impact,transition,eco,friendly,consumption,eco-positive,sustainability,biodiversity,ecoresponsible" />
        <meta property="og:title" content="Privacy policy" />
        <meta property="og:description" content="EcoShift ⁕ the digital platform for your eco-positive consumption!" />
        <meta property="og:image" content={icon} />
        <meta property="og:url" content={process.env.PUBLIC_URL + "/"} />
        <meta name="twitter:card" content={icon} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://ecoshift.eco/privacy" />
      </Helmet>

      <HeaderThree homeLink="/" logo="symbol-dark" color="color-black" alwaysSticky="true" />

      {/* Start Privacy Policy Area  */}
      <Card id="privacy" className="card_color--ugs ">
        <CardBody>
          <div className="slider-wrapper">
            <div id="whoweare" className="fix top-inset-main">
              <div className="service-area creative-service-wrapper pb--80 card_color--ugs ">
                <div className="container">
                  <br />
                  <br />
                  <br />
                  <div className="row">
                    <div className="col-lg-12 mb_sm--0 privacy-policy">
                      <p className="privacy-policy-brand">{t("privacy.brand")}</p>
                      <p className="section-title-left privacy-policy-title">{t("privacy.docTitle")}</p>
                      <p className="text-blod-description-18 text-left-important privacy-policy-subtitle">{t("privacy.docSubtitle")}</p>
                      <p className="text-left-important privacy-policy-meta">
                        {t("privacy.version")}
                        <br />
                        {t("privacy.availableIn")} <LanguageSwitcher i18n={i18n} />
                      </p>

                      {/* Section 1 */}
                      <p className="section-title-left privacy-policy-h1 mt-3">{t("privacy.s1.heading")}</p>
                      <p className="text-blod-description-18 text-left-important">{t("privacy.s1.p1")}</p>
                      <p className="text-blod-description-18 text-left-important">
                        <strong>{t("privacy.s1.controllerLabel")}</strong>
                      </p>
                      <p
                        className="text-blod-description-18 text-left-important"
                        dangerouslySetInnerHTML={{
                          __html: [
                            t("privacy.s1.companyName"),
                            t("privacy.s1.address"),
                            t("privacy.s1.companyNumber"),
                            t("privacy.s1.email"),
                            t("privacy.s1.website"),
                          ].join("<br/>"),
                        }}
                      />
                      <Html tag="p" className="text-blod-description-18 text-left-important" i18nKey="privacy.s1.scope" t={t} />

                      {/* Section 2 */}
                      <p className="section-title-left privacy-policy-h1 mt-3">{t("privacy.s2.heading")}</p>
                      <p className="text-blod-description-18 text-left-important">{t("privacy.s2.intro")}</p>

                      {["s1", "s2", "s3", "s4", "s5", "s6", "s7", "s8", "s9"].map((sub) => (
                        <div key={sub}>
                          <p className="text-blod-small-description text-left-important mt-3">
                            <strong className="privacy-policy-h2">{t(`privacy.s2.${sub}.heading`)}</strong>
                          </p>
                          <p className="text-blod-description-18 text-left-important">{t(`privacy.s2.${sub}.p1`)}</p>
                          <p className="text-blod-description-18 text-left-important">
                            <em>{t(`privacy.s2.${sub}.legal`)}</em>
                          </p>
                          {i18n.exists(`privacy.s2.${sub}.p2`) && (
                            <p className="text-blod-description-18 text-left-important">{t(`privacy.s2.${sub}.p2`)}</p>
                          )}
                        </div>
                      ))}

                      {/* Section 3 */}
                      <p className="section-title-left privacy-policy-h1 mt-3">{t("privacy.s3.heading")}</p>
                      <BulletList i18nKey="privacy.s3.items" t={t} />

                      {/* Section 4 */}
                      <p className="section-title-left privacy-policy-h1 mt-3">{t("privacy.s4.heading")}</p>
                      <p className="text-blod-description-18 text-left-important">{t("privacy.s4.intro")}</p>

                      <p className="text-blod-small-description text-left-important mt-3">
                        <strong className="privacy-policy-h2">{t("privacy.s4.s1.heading")}</strong>
                      </p>
                      <p className="text-blod-description-18 text-left-important">{t("privacy.s4.s1.intro")}</p>
                      <DataTable headersKey="privacy.s4.s1.tableHeaders" rowsKey="privacy.s4.s1.tableRows" t={t} />

                      <p className="text-blod-small-description text-left-important mt-3">
                        <strong className="privacy-policy-h2">{t("privacy.s4.s2.heading")}</strong>
                      </p>
                      <p className="text-blod-description-18 text-left-important">{t("privacy.s4.s2.p1")}</p>

                      <p className="text-blod-small-description text-left-important mt-3">
                        <strong className="privacy-policy-h2">{t("privacy.s4.s3.heading")}</strong>
                      </p>
                      <p className="text-blod-description-18 text-left-important">{t("privacy.s4.s3.p1")}</p>

                      {/* Section 5 */}
                      <p className="section-title-left privacy-policy-h1 mt-3">{t("privacy.s5.heading")}</p>
                      <p className="text-blod-description-18 text-left-important">{t("privacy.s5.p1")}</p>
                      <p className="text-blod-description-18 text-left-important">{t("privacy.s5.p2")}</p>

                      {/* Section 6 */}
                      <p className="section-title-left privacy-policy-h1 mt-3">{t("privacy.s6.heading")}</p>
                      <DataTable headersKey="privacy.s6.tableHeaders" rowsKey="privacy.s6.tableRows" t={t} />

                      {/* Section 7 */}
                      <p className="section-title-left privacy-policy-h1 mt-3">{t("privacy.s7.heading")}</p>
                      <Html tag="p" className="text-blod-description-18 text-left-important" i18nKey="privacy.s7.intro" t={t} />
                      <RightsList i18nKey="privacy.s7.items" t={t} />
                      <p className="text-blod-description-18 text-left-important">{t("privacy.s7.outro")}</p>

                      {/* Section 8 */}
                      <p className="section-title-left privacy-policy-h1 mt-3">{t("privacy.s8.heading")}</p>
                      <p className="text-blod-description-18 text-left-important">{t("privacy.s8.p1")}</p>

                      {/* Section 9 */}
                      <p className="section-title-left privacy-policy-h1 mt-3">{t("privacy.s9.heading")}</p>
                      <p className="text-blod-description-18 text-left-important">{t("privacy.s9.p1")}</p>

                      {/* Section 10 */}
                      <p className="section-title-left privacy-policy-h1 mt-3">{t("privacy.s10.heading")}</p>
                      <Html tag="p" className="text-blod-description-18 text-left-important" i18nKey="privacy.s10.p1" t={t} />

                      {/* Section 11 */}
                      <p className="section-title-left privacy-policy-h1 mt-3">{t("privacy.s11.heading")}</p>
                      <p className="text-blod-description-18 text-left-important">{t("privacy.s11.intro")}</p>
                      <DataTable headersKey="privacy.s11.tableHeaders" rowsKey="privacy.s11.tableRows" t={t} />
                      <p className="text-blod-description-18 text-left-important">{t("privacy.s11.outro")}</p>

                      {/* Section 12 */}
                      <p className="section-title-left privacy-policy-h1 mt-3">{t("privacy.s12.heading")}</p>
                      <p className="text-blod-description-18 text-left-important">{t("privacy.s12.p1")}</p>
                      <BulletList i18nKey="privacy.s12.items" t={t} />
                      <p className="text-blod-description-18 text-left-important">{t("privacy.s12.p2")}</p>
                      <p className="text-blod-description-18 text-left-important">{t("privacy.s12.p3")}</p>
                      <p
                        className="text-blod-description-18 text-left-important"
                        dangerouslySetInnerHTML={{
                          __html: [
                            t("privacy.s12.authorityName"),
                            t("privacy.s12.authorityAddress"),
                            t("privacy.s12.authorityWebsite"),
                          ].join("<br/>"),
                        }}
                      />

                      {/* Section 13 */}
                      <p className="section-title-left privacy-policy-h1 mt-3">{t("privacy.s13.heading")}</p>
                      <p className="text-blod-description-18 text-left-important">{t("privacy.s13.p1")}</p>
                      <Html tag="p" className="text-blod-description-18 text-left-important" i18nKey="privacy.s13.p2" t={t} />

                      {/* Section 14 */}
                      <p className="section-title-left privacy-policy-h1 mt-3">{t("privacy.s14.heading")}</p>
                      <p className="text-blod-description-18 text-left-important">{t("privacy.s14.p1")}</p>
                      <p className="text-blod-description-18 text-left-important">
                        {t("privacy.s14.linksLabel")} <LanguageSwitcher i18n={i18n} />
                      </p>

                      <br />
                      <Html tag="p" className="text-blod-description-18 text-left-important" i18nKey="privacy.footer.line1" t={t} />
                    </div>
                  </div>
                  <br />
                </div>
              </div>
            </div>
          </div>
        </CardBody>
      </Card>
      {/* End Privacy Policy Area  */}
    </div>
  );
};

export default PrivacyPage;
